import { useEffect, useMemo } from 'react';
import { usePublicSettings } from '../context/PublicSettingsContext.js';
import { useStorefront } from '../context/StoreContext.js';
import { resolveMediaUrl } from '../utils/mediaUrl.js';

/** Icon shipped in /public — used until the store's own favicon resolves, and if it fails. */
const FALLBACK_FAVICON = `${process.env.PUBLIC_URL || ''}/logo.png`;

function guessIconType(url) {
    const path = String(url || '').split('?')[0].split('#')[0].toLowerCase();
    if (path.endsWith('.ico')) return 'image/x-icon';
    if (path.endsWith('.png')) return 'image/png';
    if (path.endsWith('.webp')) return 'image/webp';
    if (path.endsWith('.svg')) return 'image/svg+xml';
    if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
    if (path.endsWith('.gif')) return 'image/gif';
    // R2 (and legacy S3/CloudFront) keys often carry no extension; let the browser sniff it.
    return '';
}

function getLink(selector, rel) {
    let link = document.querySelector(selector);
    if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
    }
    return link;
}

function getIconLink() {
    return getLink('link[rel~="icon"]', 'icon');
}

/** iOS reads this at "Add to Home Screen" time, so updating it at runtime works. */
function getAppleTouchIconLink() {
    return getLink('link[rel="apple-touch-icon"]', 'apple-touch-icon');
}

/**
 * Points the browser-tab icon at the merchant's uploaded favicon and the iOS
 * home-screen icon at their logo. Both fall back to the bundled defaults.
 */
export function useStoreFavicon() {
    const { publicSettings } = usePublicSettings();
    const { organization } = useStorefront();

    const faviconUrl = useMemo(() => {
        return (
            resolveMediaUrl(publicSettings?.faviconUrl) ||
            resolveMediaUrl(organization?.settings?.faviconUrl) ||
            resolveMediaUrl(publicSettings?.logoUrl) ||
            ''
        );
    }, [publicSettings?.faviconUrl, publicSettings?.logoUrl, organization?.settings?.faviconUrl]);

    // iOS ignores the favicon for home-screen icons and has no use for a 32px
    // .ico, so the logo comes first here — the reverse of the tab icon.
    const appleTouchIconUrl = useMemo(() => {
        return (
            resolveMediaUrl(publicSettings?.logoUrl) ||
            resolveMediaUrl(organization?.settings?.logoUrl) ||
            resolveMediaUrl(publicSettings?.faviconUrl) ||
            ''
        );
    }, [publicSettings?.logoUrl, publicSettings?.faviconUrl, organization?.settings?.logoUrl]);

    useEffect(() => {
        if (!appleTouchIconUrl) return undefined;

        const link = getAppleTouchIconLink();
        const previousHref = link.getAttribute('href');

        const probe = new Image();
        let cancelled = false;
        probe.onload = () => {
            if (!cancelled) link.setAttribute('href', appleTouchIconUrl);
        };
        probe.src = appleTouchIconUrl;

        return () => {
            cancelled = true;
            probe.onload = null;
            if (previousHref) link.setAttribute('href', previousHref);
        };
    }, [appleTouchIconUrl]);

    useEffect(() => {
        if (!faviconUrl) return undefined;

        const link = getIconLink();
        const previousHref = link.getAttribute('href');
        const previousType = link.getAttribute('type');

        // Verify the icon loads before swapping — a broken href leaves the tab
        // with the browser's blank-page icon, which looks worse than our default.
        const probe = new Image();
        let cancelled = false;

        const apply = (href) => {
            if (cancelled) return;
            const type = guessIconType(href);
            if (type) {
                link.setAttribute('type', type);
            } else {
                link.removeAttribute('type');
            }
            link.setAttribute('href', href);
        };

        probe.onload = () => apply(faviconUrl);
        probe.onerror = () => apply(FALLBACK_FAVICON);
        probe.src = faviconUrl;

        return () => {
            cancelled = true;
            probe.onload = null;
            probe.onerror = null;
            if (previousHref) {
                link.setAttribute('href', previousHref);
            }
            if (previousType) {
                link.setAttribute('type', previousType);
            } else {
                link.removeAttribute('type');
            }
        };
    }, [faviconUrl]);
}

export default useStoreFavicon;
